import { useCallback, type RefObject } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import type { ActionInputHandle } from '@bonusly/common-action-input';

import { DELETED_ENTIRELY_RETAINED_RATIO_THRESHOLD, levenshteinDistance, retainedRatio } from '../../../../lib/analytics/levenshtein';
import type { useDraftAssist } from './use-draft-assist';

type DraftAssist = ReturnType<typeof useDraftAssist>;

type UseDraftAssistRetentionOptions = {
	draftAssist: DraftAssist;
	inputRef: RefObject<ActionInputHandle>;
};

type UseDraftAssistRetentionResult = {
	/**
	 * Inserts the suggestion text with the AI highlight, mints a stable track id, and
	 * tells `useDraftAssist` to record the accept against that id. Safe to call with
	 * an empty suggestion (no-op) and when the editor handle is not yet mounted.
	 */
	insertSuggestion: (suggestion: string) => Promise<void>;
	/**
	 * Reads the live edit state of every accepted suggestion via the editor handle and
	 * fires:
	 *   - one `SuggestionEditedAfterAccept` per accepted suggestion that has changed,
	 *     including `acceptIndex`/`acceptCount` so per-position trends are queryable.
	 *   - one `SessionSuggestionRetention` per session as a length-weighted aggregate
	 *     across all accepts (a longer accepted suggestion influences the ratio more
	 *     than a shorter one).
	 *
	 * Should be called once on submit success, before any clearing/reset of the editor.
	 * No-op when no suggestions were accepted in the session.
	 */
	reportRetention: () => void;
};

/**
 * Owns the analytics-side bookkeeping for accepted Draft Assist suggestions:
 * minting per-accept track ids at insert time and emitting per-accept + aggregate
 * retention events at submit time. Uses ProseMirror position tracking under the
 * hood (see `SuggestionHighlight`'s `suggestion-position-tracker` plugin).
 */
export function useDraftAssistRetention({ draftAssist, inputRef }: UseDraftAssistRetentionOptions): UseDraftAssistRetentionResult {
	const { track } = useAnalytics();
	// Held in a const so the deps array of insertSuggestion is honest about what changes.
	const acceptedSuggestionsRef = draftAssist.acceptedSuggestionsRef as RefObject<DraftAssist['acceptedSuggestionsRef']['current']>;
	const conversationIdRef = draftAssist.conversationIdRef;

	const insertSuggestion = useCallback(
		async (suggestion: string) => {
			if (!suggestion) return;
			const input = inputRef.current;
			if (!input) return;
			const trackId = crypto.randomUUID();
			input.insertWithHighlight(suggestion, {
				trackId,
				dimensionKey: draftAssist.dimensionKey ?? null,
				conversationId: conversationIdRef?.current ?? null,
			});
			await draftAssist.accept(trackId);
		},
		[inputRef, draftAssist.dimensionKey, draftAssist.accept, conversationIdRef]
	);

	const reportRetention = useCallback(() => {
		const accepted = acceptedSuggestionsRef.current ?? [];
		if (accepted.length === 0) return;

		const edits = inputRef.current?.getAcceptedSuggestionEdits() ?? [];
		const editById = new Map(edits.map((e) => [e.id, e]));

		let totalWeightedRatio = 0;
		let totalAcceptedChars = 0;

		accepted.forEach((entry, index) => {
			const edit = editById.get(entry.trackId);
			const currentText = edit?.currentText ?? '';
			// Grapheme-aware lengths so single emoji edits aren't double-counted by UTF-16 indexing.
			const originalLen = Array.from(entry.text).length;
			const distance = levenshteinDistance(entry.text, currentText);
			const ratio = retainedRatio(entry.text, currentText, distance);

			totalAcceptedChars += originalLen;
			// Weight each suggestion's ratio by its original length so longer suggestions
			// influence the aggregate more than shorter ones.
			totalWeightedRatio += ratio * originalLen;

			if (distance > 0) {
				track(AnalyticsEvent.DraftAssist.SuggestionEditedAfterAccept, {
					dimensionKey: entry.dimensionKey,
					conversationId: entry.conversationId,
					editDistance: distance,
					retainedRatio: ratio,
					deletedEntirely: ratio <= DELETED_ENTIRELY_RETAINED_RATIO_THRESHOLD,
					acceptIndex: index + 1,
					acceptCount: accepted.length,
					source: 'gratitude_gauge',
				});
			}
		});

		const aggregateRatio = totalAcceptedChars === 0 ? 1 : totalWeightedRatio / totalAcceptedChars;
		track(AnalyticsEvent.DraftAssist.SessionSuggestionRetention, {
			conversationId: accepted[0]?.conversationId ?? null,
			acceptCount: accepted.length,
			totalAcceptedChars,
			retainedRatio: aggregateRatio,
			deletedEntirely: aggregateRatio <= DELETED_ENTIRELY_RETAINED_RATIO_THRESHOLD,
			source: 'gratitude_gauge',
		});
	}, [acceptedSuggestionsRef, inputRef, track]);

	return { insertSuggestion, reportRetention };
}
